import useSWR from 'swr';
import { useState, useCallback } from 'react';
import { outletApi } from 'src/api/outletApi';

const allOutletsFetcher = () => outletApi.fetchAllOutlets();
const outletsFetcher = () => outletApi.fetchOutlets();

export const useAllOutlets = (initialLimit = 100) => {
  const [limit, setLimit] = useState(initialLimit);

  // Fetch all outlets but process them progressively
  const { data, error, isLoading, mutate } = useSWR('allOutlets', allOutletsFetcher);

  // Get the full dataset (for searching)
  const fullData = useCallback(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : [];
  }, [data]);

  // Process only a subset of the data based on the current limit
  const processedData = useCallback(() => {
    if (!data) return [];
    if (!Array.isArray(data)) return [];

    // Return only the first 'limit' items
    return data.slice(0, limit);
  }, [data, limit]);

  // Function to load more data
  const loadMore = useCallback((additionalItems = 100) => {
    setLimit((prevLimit) => prevLimit + additionalItems);
  }, []);

  // Check if there are more items to load
  const hasMore = useCallback(() => {
    if (!data) return false;
    if (!Array.isArray(data)) return false;

    return limit < data.length;
  }, [data, limit]);

  // Ensure we're returning the data in the correct format
  return {
    outlets: processedData(),
    allOutlets: fullData(), // Full dataset for searching
    error,
    loading: isLoading,
    mutate,
    loadMore,
    hasMore: hasMore(),
    totalCount: data && Array.isArray(data) ? data.length : 0,
  };
};

export const useOutlets = () => {
  const { data, error, isLoading, mutate } = useSWR('outlets', outletsFetcher);

  // Ensure we're returning the data in the correct format
  return {
    outlets: data,
    error,
    loading: isLoading,
    mutate,
  };
};
